import { html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import tailwindStyles from "../../../index.css?inline";
import { FormBase } from "./formBase";
// Make sure the custom stepper is imported and registered
import "../custom-stepper";

interface FormData {
  _details: any;
  title?: string;
  // Add other properties that might exist on af/feed objects
}

@customElement('container-form')
export class ContainerForm extends FormBase {
  static styles = [
    unsafeCSS(tailwindStyles),
    css`
      /* Keep your existing styles */
      .animate__animated {
        animation-duration: 0.7s;
      }
      
      .right-out-left-in-animation {
        animation-name: slideOutRight, slideInLeft;
      }
      
      .left-out-right-in-animation {
        animation-name: slideOutLeft, slideInRight;
      }
      
      @keyframes slideOutRight {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
      
      @keyframes slideInLeft {
        from { transform: translateX(-100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      
      @keyframes slideOutLeft {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(-100%); opacity: 0; }
      }
      
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      /* Container with Tailwind-like responsive classes */
      .form-container {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: 1rem;
        padding-right: 1rem;
        display: flex;
        flex-direction: column;
        height: 100%;
        max-width: 100%;
      }

      /* Tailwind container breakpoints */
      @media (min-width: 640px) {
        .form-container {
          max-width: 640px;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }
      }

      @media (min-width: 768px) {
        .form-container {
          max-width: 768px;
        }
      }

      @media (min-width: 1024px) {
        .form-container {
          max-width: 1024px;
          padding-left: 2rem;
          padding-right: 2rem;
        }
      }

      @media (min-width: 1280px) {
        .form-container {
          max-width: 1280px;
        }
      }

      @media (min-width: 1536px) {
        .form-container {
          max-width: 1536px;
        }
      }


      /* Card styles */
      .form-x-card {
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 100%;
        overflow: hidden;
        background: white;
        border-radius: 0.5rem;
        border: 1px solid #e5e7eb;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        width: 100%;
      }

      /* No shadow for payments page */
      .form-x-card.shadow-none {
        box-shadow: none;
      }

      .header-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: var(--md-sys-color-onSurface, #1a1a1a);
        flex-shrink: 0;
      }

      /* Card content with scrollable area */
      .card-content {
        position: relative;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      /* Custom scrollbar for card content */
      .card-content::-webkit-scrollbar {
        width: 6px;
      }

      .card-content::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      .card-content::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }

      .card-content::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }

      .card-content.scrollable {
        overflow-y: auto;
      }

      .card-content:not(.scrollable) {
        overflow-y: visible;
      }

      /* Ensure card content takes full height */
      .card-content {
        height: 100%;
        min-height: 300px;
      }

      .overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(255, 255, 255, 0.7);
        z-index: 10;
        border-radius: 0.5rem;
      }

      .pro-container {
        padding: 0.5rem 1.5rem;
        flex-shrink: 0;
      }

      /* Scrollbar styles for dialog */
      .scrollbar-thin::-webkit-scrollbar {
        width: 6px;
      }

      .scrollbar-thin::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      .scrollbar-thin::-webkit-scrollbar-thumb {
        background: #9ca3af;
        border-radius: 3px;
      }

      .scrollbar-thin::-webkit-scrollbar-thumb:hover {
        background: #6b7280;
      }

      /* Stepper styles - fixed at top */
      .stepper-wrapper {
        margin-bottom: 1.5rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 5;
        width: 100%;
      }

      .stepper-container {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        flex-wrap: wrap;
        padding: 0.5rem 0;
        width: 100%;
      }

      /* Ensure custom stepper is visible */
      custom-stepper {
        display: block;
        width: 100%;
      }

      /* Slot container - takes remaining space */
      .slot-container {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      /* Ensure e-list takes full height */
      .slot-container ::slotted(e-list) {
        height: 100%;
        flex: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      /* Responsive padding for mobile */
      @media (max-width: 640px) {
        .card-content {
          padding: 1rem 0.75rem;
        }

        .header-title {
          font-size: 1.1rem;
        }

        .stepper-wrapper {
          margin-bottom: 1rem;
          padding: 0.25rem 0;
        }

        .form-container {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
      }

      @media (max-width: 480px) {
        .card-content {
          padding: 0.75rem 0.5rem;
        }

        .header-title {
          font-size: 1rem;
        }

        .form-container {
          padding-left: 0.5rem;
          padding-right: 0.5rem;
        }
      }

      /* Height management */
      .form-x-card {
        max-height: calc(100vh - 2rem);
      }

      @media (max-width: 640px) {
        .form-x-card {
          max-height: calc(100vh - 1rem);
        }
      }

      /* Ensure the card doesn't overflow on mobile */
      .card {
        overflow: hidden;
      }

      /* Fix for canvas template */
      :host([template="canvas"]) .form-container {
        max-width: 100% !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      :host([template="canvas"]) .form-x-card {
        max-height: 100vh;
        border-radius: 0;
        border: none;
        box-shadow: none;
      }

      :host([template="canvas"]) .card-content {
        padding: 1rem 1.5rem;
      }

      /* Ensure proper height when scrolling */
      .scroll-container {
        height: 100%;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }

      /* Center content wrapper */
      .content-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        height: 100%;
      }

      /* Ensure slot content is properly centered */
      .slot-container ::slotted(*) {
        width: 100%;
        max-width: 100%;
      }
    `
  ];

  @property({ type: Number }) pos = 0;
  @property({ type: Number }) maxPosReached = 0;
  @property({ type: String }) logo = '';
  @property({ type: String }) headerBgColor = '';
  @property({ type: String }) details = '';
  @property({ type: Boolean }) paymentsPage = false;
  @property({ type: String }) loading = 'false';
  @property({ type: String }) height = '';
  @property({ type: String }) maxHeight = '';
  @property({ type: Boolean }) isSectionPage = false;
  @property({ type: Array }) sections: any[] = [];
  @property({ type: Object }) af: FormData = {
    _details: undefined
  };
  @property({ type: Object }) feed: FormData = {
    _details: undefined
  };
  @property({ type: String }) template = '';

  private _stepLabels: string[] = [];

  firstUpdated() {
    this.setCardContentHeight();
    this._initializeStepLabels();
    this._applyContainerClasses();
    
    console.log('ContainerForm initialized:', {
      pos: this.pos,
      maxPosReached: this.maxPosReached,
      sections: this.sections?.length,
      stepLabels: this._stepLabels,
      template: this.template || window.TEMPLATE
    });
  }

  updated(changedProperties: Map<string, any>) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "pos" && !isNaN(oldValue)) {
        this.positionTransition(oldValue);
      }
    });
    
    // Log when stepper should render
    // if (changedProperties.has('pos') || changedProperties.has('maxPosReached')) {
    //   console.log('Stepper conditions:', {
    //     pos: this.pos,
    //     maxPosReached: this.maxPosReached,
    //     shouldShowStepper: this.shouldShowStepper(),
    //     stepCount: this.maxPosReached + 1
    //   });
    // }

    if (changedProperties.has('details') || changedProperties.has('af') || changedProperties.has('feed')) {
      this._initializeStepLabels();
    }
  }

  private _applyContainerClasses() {
    // Add template class to container for canvas-specific styling
    const container = this.shadowRoot?.querySelector('.form-container');
    if (container) {
      const template = this.template || (window as any).TEMPLATE || '';
      if (template === 'canvas') {
        container.classList.add('canvas');
      } else {
        container.classList.remove('canvas');
      }
    }
  }

  private _initializeStepLabels() {
    try {
      // Try to get step labels from details
      if (this.details) {
        const detailsObj = typeof this.details === 'string' ? JSON.parse(this.details) : this.details;
        if (detailsObj.stepLabels && Array.isArray(detailsObj.stepLabels)) {
          this._stepLabels = detailsObj.stepLabels;
          return;
        }
      }

      // Try to get from af or feed
      const source = this.af || this.feed || {};
      if (source._details?.stepLabels) {
        this._stepLabels = source._details.stepLabels;
        return;
      }

      // Default labels
      if (this.sections && this.sections.length > 0) {
        this._stepLabels = this.sections.map((section, index) => 
          section.title || `Step ${index + 1}`
        );
      }
    } catch (e) {
      console.warn('Could not parse step labels:', e);
    }
  }

  // Helper method to check if stepper should be shown
  private shouldShowStepper(): boolean {
    // Show stepper if:
    // 1. We have sections
    // 2. Either pos > 0 or maxPosReached > 0 (we've moved beyond first step)
    // 3. OR we have more than 1 section (always show if multiple steps)
    const hasMultipleSections = this.sections && this.sections.length > 1;
    const hasProgress = this.pos > 0 || this.maxPosReached >= 1;
    
    // Force show if we have more than 1 section
    if (hasMultipleSections) {
      return true;
    }
    
    // Otherwise show based on progress
    return hasProgress;
  }

  positionTransition(oldPosition: number) {
    const formXCard = this.shadowRoot?.querySelector(".form-x-card") as HTMLElement;
    if (!formXCard) return;

    const triggerTransition = (direction: string, oppositeDirection: string) => {
      formXCard.classList.add(`${direction}-out-${oppositeDirection}-in-animation`, 'animate__animated');

      setTimeout(() => {
        formXCard.classList.remove(`${direction}-out-${oppositeDirection}-in-animation`, 'animate__animated');
      }, 700);
    };
    
    if (this.pos > oldPosition) {
      triggerTransition("right", "left");
    }

    if (this.pos < oldPosition) {
      triggerTransition("left", "right");
    }
  }

  renderStepper() {
    // Show stepper condition
    if (!this.shouldShowStepper()) {
      return html``;
    }

    // Calculate steps count
    const stepCount = Math.max(
      this.maxPosReached + 1,
      this.sections?.length || 0
    );
    
    const steps = Array.from({ length: stepCount });
    
    // Get step labels
    const stepLabels = this._stepLabels.length > 0 
      ? this._stepLabels 
      : steps.map((_, index) => `Step ${index + 1}`);

    console.log('Rendering stepper with:', {
      stepCount,
      activeStep: this.pos,
      labels: stepLabels,
      sections: this.sections?.length
    });

    return html`
      <div class="stepper-wrapper">
        <div class="stepper-container">
          <custom-stepper
            .steps="${steps}"
            .activeStep="${this.pos}"
            .currentStepLabel="${this.getStepLabel(this.pos)}"
            .feeds="${this.sections || []}"
            @step-click="${this._onStepClick}"
          ></custom-stepper>
        </div>
      </div>
    `;
  }

  getStepLabel(index: number) {
    if (this._stepLabels && this._stepLabels[index]) {
      return this._stepLabels[index];
    }
    return `Step ${index + 1}`;
  }

  render() {
    const template = this.template || (window as any).TEMPLATE || '';
    const isCanvas = template === 'canvas';
    
    // Determine container classes - using Tailwind-like responsive classes
    let containerClasses = 'form-container container mx-auto px-4';
    
    if (isCanvas) {
      containerClasses += ' canvas';
    } else {
      // Using Tailwind container classes with proper breakpoints
      containerClasses += ' container mx-auto px-4 sm:px-6 lg:px-8';
    }

    // Get custom classes from details if provided
    try {
      const detailsObj = typeof this.details === 'string' ? JSON.parse(this.details) : this.details;
      if (detailsObj.classes) {
        containerClasses += ` ${detailsObj.classes}`;
      }
    } catch (e) {
      // Ignore parsing errors
    }

    // Card classes with proper styling
    const cardClasses = `relative ${isCanvas ? 'rounded-none border-0' : ''}`;

    // Determine max height for the card
    let maxHeightStyle = '';
    if (this.maxHeight) {
      maxHeightStyle = `max-height: ${this.maxHeight};`;
    } else if (isCanvas) {
      maxHeightStyle = 'max-height: 100vh;';
    } else {
      maxHeightStyle = 'max-height: calc(100vh - 2rem);';
    }

    return html`
      <div class="${containerClasses}">
        <div class="${cardClasses}" style="${maxHeightStyle} ${this._getCardStyle()}">
          ${this.checkLoading() ? html`
            <div class="overlay"></div>
          ` : ''}
          
          ${this.checkLoading() ? html`
            <div class="pro-container">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-blue-600 h-2 rounded-full animate-pulse"></div>
              </div>
            </div>
          ` : ''}
          
          <div class="card-content ${this.checkForDialog()}">
            ${this.af?.title || this.feed?.title ? html`
              <p class="header-title">
                ${this.af?.title || this.feed?.title}
              </p>
            ` : ''}

            ${this.renderStepper()}

            <div class="slot-container">
              <slot name="body"></slot>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _getCardStyle(): string {
    const styles: string[] = [];
    
    if (this.height) {
      styles.push(`height: ${this.height}`);
    }
    
    // If maxHeight is not set, we handle it in the render method
    // But we still need to apply any additional styles
    
    return styles.join('; ');
  }

  setCardContentHeight() {
    let section = document.querySelector("section-page");

    if (this.maxHeight || this.height) {
      if (section != null && section.id == "dialog" && window.TEMPLATE == 'service') {
        const cardContent = this.shadowRoot?.querySelector(".card-content") as HTMLElement;
        if (cardContent) {
          if (this.height) cardContent.style.height = this.height;
          if (this.maxHeight) cardContent.style.maxHeight = this.maxHeight;
        }
      }
    }
  }

  _notifyPosChange(newPos: number) {
    this.dispatchEvent(new CustomEvent('pos-change', {
      bubbles: true,
      composed: true,
      detail: { pos: newPos },
    }));
  }

  _onStepClick(event: CustomEvent<{ index: number }>) {
    const index = event.detail.index;
    this._goToStep(index);
  }

  _goToStep(index: number) {
    if (index < 0 || index >= (this.sections?.length || 0)) return;
    
    // Check if we're going backwards
    if (index < this.pos) {
      // Dispatch event that will trigger FormRender to clear future steps
      this.dispatchEvent(new CustomEvent('step-navigate-back', {
        bubbles: true,
        composed: true,
        detail: { newPos: index }
      }));
    }
    
    this.pos = index;
    this._notifyPosChange(this.pos);
  }

  _hideF(p: number, s: any[]) {
    return p >= s.length - 1;
  }

  checkForDialog() {
    return this.isSectionPage ? "overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100" : "scrollable";
  }

  checkLoading() {    
    return this.loading === "true";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'container-form': ContainerForm;
  }
}